import React, { useState } from 'react';
import { useDrop } from 'react-dnd';
import { ItemTypes } from '../constants/appConstants';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Collapse,
  List,
  Box,
  Tooltip
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import { useTranslation } from 'react-i18next';

import ProjectSettings from './ProjectSettings';
import { useMessageHandler } from '../contexts/MessageContext';
import { getModeBasedColor } from '../themes/migration-helpers';

const ProjectItem = ({
  project,
  isExpanded,
  onToggleExpand,
  onOpenMenu,
  onMoveSession,
  projectSessions,
  renderSession,
  onCreateNewChat
}) => {
  const { t } = useTranslation();
  const { isInteracting } = useMessageHandler();
  const [showSettings, setShowSettings] = useState(false);

  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.CHAT_SESSION,
    drop: (item) => onMoveSession(item.sessionId, project.id),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop()
    })
  }));

  const handleCreateNewChat = (e) => {
    e.stopPropagation();
    if (onCreateNewChat) {
      onCreateNewChat(project.id);
    }
  };

  const handleOpenSettings = (e) => {
    e.stopPropagation();
    setShowSettings(true);
  };

  return (
    <>
      <div
        ref={drop}
        data-testid={`project-item-${project.id}`}
        style={{
          backgroundColor: isOver && canDrop ?
            `${project.color}22` : // Add slight transparency
            'transparent',
          transition: 'background-color 0.3s',
          borderRadius: '4px'
        }}
      >
        <ListItem
          button
          onClick={() => onToggleExpand(project.id)}
          sx={{
            pl: 1,
            pr: 0.5,
            mb: 0.5,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <FolderIcon sx={{ color: project.color || '#9c27b0' }} />
          </ListItemIcon>

          <ListItemText
            primary={project.name}
            primaryTypographyProps={{
              noWrap: true,
              sx: {
                fontSize: '0.85rem',
                fontWeight: 'medium',
                mr: 1
              }
            }}
            sx={{ flex: '1 1 auto', overflow: 'hidden' }}
            data-testid={`project-name-${project.id}`}
          />

          {/* Context indicator badge */}
          {project.contextEnabled && (
            <Tooltip title={t('projects.contextEnabledTooltip')}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  mr: 1
                }}
              />
            </Tooltip>
          )}

          {/* Add action buttons before the context menu */}
          <Box sx={{ display: 'flex', mr: 0.5 }}>
            <Tooltip title={t('projects.newChat')}>
              <IconButton
                size="small"
                onClick={handleCreateNewChat}
                disabled={isInteracting}
                data-testid="new-chat-button"
                sx={{
                  opacity: 0.7,
                  '&:hover': { opacity: 1 }
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={t('projects.settings.title', { name: '' })}>
              <IconButton
                size="small"
                onClick={handleOpenSettings}
                disabled={isInteracting}
                sx={{
                  opacity: 0.7,
                  '&:hover': { opacity: 1 }
                }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <IconButton
            edge="end"
            size="small"
            onClick={(e) => onOpenMenu(e, project.id)}
            sx={{ marginLeft: 'auto' }}
            data-testid={`project-menu-${project.id}`}
          >
            <MoreVertIcon
              fontSize="small"
            />
          </IconButton>
        </ListItem>
      </div>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <List
          component="div"
          disablePadding
          sx={(theme) => ({
            maxHeight: '200px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: getModeBasedColor(theme, '#00000033', '#ffffff33'),
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: getModeBasedColor(theme, '#0000004d', '#ffffff4d'),
            }
          })}
        >
          {projectSessions.length > 0 ? (
            projectSessions.map(session => renderSession(session, true, project.id))
          ) : (
            <ListItem sx={{ pl: 4 }}>
              <ListItemText
                primary={t('projects.noSessions')}
                primaryTypographyProps={{
                  sx: { fontSize: '0.75rem', color: 'text.secondary', fontStyle: 'italic' }
                }}
              />
            </ListItem>
          )}
        </List>
      </Collapse>

      {/* Settings Dialog */}
      <ProjectSettings
        open={showSettings}
        onClose={() => setShowSettings(false)}
        project={project}
      />
    </>
  );
};

export default ProjectItem;